import React, { useState } from "react";
import { useAuth } from "./auth";
import { useNavigate } from "react-router-dom";
import axios, { AxiosRequestConfig } from "axios";
import { ProgressSpinner } from "primereact/progressspinner";
import { BlockUI } from "primereact/blockui";
import authService from "../services/auth/auth.service";
import { RefreshTokenDto } from "../services/auth/auth.dto";
import { useToast } from "./toast";

let AxiosContext = React.createContext(null);
export function useAxiosContext() {
  return React.useContext(AxiosContext);
}

// Define the structure of a retry queue item
interface RetryQueueItem {
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
  config: AxiosRequestConfig;
}

export function AxiosProvider({ children }: { children: React.ReactNode }) {
  const toast = useToast();
  const auth = useAuth();
  let navigate = useNavigate();
  const [blocked, setBlocked] = useState<boolean>(false);
  const [isAxiosInterceptorAdded, setIsAxiosInterceptorAdded] = useState<boolean>(false);
  const authRef = React.useRef(auth);
  const toastRef = React.useRef(toast);
  const refreshAndRetryQueueRef = React.useRef<RetryQueueItem[]>([]);
  const isRefreshingRef = React.useRef(false);

  // The interceptor is registered once, so keep the latest auth context in refs.
  authRef.current = auth;
  toastRef.current = toast;

  if (!isAxiosInterceptorAdded) {
    axios.interceptors.request.use(
      (config) => {
        const accessToken = authRef.current.getUser().access_token;
        if (accessToken && !config.headers?.Authorization) {
          config.headers = config.headers ?? {};
          config.headers.Authorization = "Bearer " + accessToken;
        }

        if (config.blockUI === undefined) {
          if (
            config.method === "post" ||
            //config.method === "get" || //spinner
            config.method === "put" ||
            config.method === "delete"
          ) {
            setBlocked(true);
            config.blockUI = true;
          }
        } else {
          setBlocked(config.blockUI);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    axios.interceptors.response.use(
      (response) => {
        if (response.config.blockUI) {
          setBlocked(false);
        }
        return response;
      },
      async (error) => {
        const originalRequest: AxiosRequestConfig = error.config;
        if (error.config.blockUI) {
          setBlocked(false);
        }
        if (error.config?.skipAuthRefresh) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401) {
          if (!isRefreshingRef.current) {
            isRefreshingRef.current = true;
            try {
              const user = authRef.current.getUser();
              if (!user.refresh_token) {
                throw new Error("Refresh token bulunamadı.");
              }

              const data: RefreshTokenDto = {
                token: user.refresh_token
              };
              const newToken = await authService.CreateTokenByRefreshToken(data, {
                blockUI: false,
                skipAuthRefresh: true
              });

              if (newToken.result && newToken.result.data) {
                authRef.current.setToken(newToken.result.data, true);
                error.config.headers["Authorization"] = "Bearer " + newToken.result.data.access_token;

                // Retry all requests in the queue with the new token
                refreshAndRetryQueueRef.current.forEach(({ config, resolve, reject }) => {
                  if (newToken.result && config.headers) {
                    // override the bearer token
                    config.headers["Authorization"] = "Bearer " + newToken.result.data.access_token;
                    axios
                      .request(config)
                      .then((response) => resolve(response))
                      .catch((err) => reject(err));
                  }
                });

                // Clear the queue
                refreshAndRetryQueueRef.current.length = 0;

                // Retry the original request
                return axios(originalRequest);
              } else {
                authRef.current.removeUserFromStorage();
                navigate("/loginsebis", { replace: true });
                return Promise.reject(error);
              }
            } catch (error) {
              authRef.current.removeUserFromStorage();
              navigate("/loginsebis", { replace: true });
              return Promise.reject(error);
            } finally {
              isRefreshingRef.current = false;
            }
          }

          // Add the original request to the queue
          return new Promise<void>((resolve, reject) => {
            refreshAndRetryQueueRef.current.push({
              config: originalRequest,
              resolve,
              reject
            });
          });
        }

        if (error.config?.overrideError) {
          return Promise.reject(error);
        }

        if (error.response?.status === 403) {
          authRef.current.removeUserFromStorage();
          navigate("/access-denied", { replace: true });
          return Promise.reject(error);
        } else if (error.response?.status === 400 || error.response?.status === 404) {
          if (error.response?.data && error.response?.data.error.isShow) {
            toastRef.current.showErrorsToast(error.response?.data.error);
          }
          return Promise.reject(error);
        } else {
          toastRef.current.show("SomethingWentWrong", "error");
          return Promise.reject(error);
        }
      }
    );

    setIsAxiosInterceptorAdded(true);
  }

  const pageLoader = () => {
    return (
      <div className="flex flex-column align-items-center justify-content-center w-screen h-screen">
        <ProgressSpinner />
      </div>
    );
  };

  return (
    <>
      <AxiosContext.Provider value={null}>
        <BlockUI blocked={blocked} fullScreen template={pageLoader}>
          {children}
        </BlockUI>
      </AxiosContext.Provider>
    </>
  );
}
